import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

// Re-validate at most once per this window while the user is active, so we don't probe Auth0 on
// every single click. Tune lower for snappier logout detection, higher for less load.
const CHECK_THROTTLE_MS = 30 * 1000;

export function SessionChecker() {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();

  const inFlight = useRef(false);
  const lastCheck = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Skip on localhost. There the SPA is cross-site with Auth0, so the silent cacheMode:"off" probe
    // below can't read the session cookie (3rd-party-cookie blocking) and falsely returns login_required —
    // which would log the user out on the first click. In deployed envs the apps share the
    // liquiditycube.net parent domain (same-site), so the probe is reliable and this check is active.
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return;

    // Re-validate the SHARED Auth0 session whenever the user interacts (click / keypress / returning to
    // the tab). If they logged out of another app (e.g. LQMyCube), the next interaction here detects it
    // and logs them out — no background polling, driven entirely by user activity.
    const checkSession = async () => {
      const now = Date.now();
      // Throttle: skip if a check is in flight or one ran very recently.
      if (inFlight.current || now - lastCheck.current < CHECK_THROTTLE_MS) return;
      inFlight.current = true;
      lastCheck.current = now;

      try {
        // cacheMode:"off" forces a real check against the Auth0 session, not the cached localStorage token.
        await getAccessTokenSilently({ cacheMode: "off" });
      } catch (e: any) {
        if (e.error === "login_required" || e.error === "consent_required") {
          toast.warning("Your session has ended. Logging you out…", { autoClose: 3000 });
          // End the local SPA session (openUrl:false = no extra /v2/logout round-trip) so isAuthenticated
          // flips to false and the app shows the Login UI.
          logout({ openUrl: false });
        }
      } finally {
        inFlight.current = false;
      }
    };

    const onActivity = () => { void checkSession(); };
    const onVisible = () => { if (document.visibilityState === "visible") void checkSession(); };

    document.addEventListener("click", onActivity);
    document.addEventListener("keydown", onActivity);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      document.removeEventListener("click", onActivity);
      document.removeEventListener("keydown", onActivity);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  return null;
}
